import React, { useState } from 'react';
import { CheckIcon, XIcon, Edit2Icon, StarIcon, BookOpenIcon, AlertCircleIcon, SearchIcon, FilterIcon, ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { ImportedBookRecord } from './types';
import { mockImportedRecords } from './mockData';
interface ImportPreviewQueueProps {
  onComplete: () => void;
}
export const ImportPreviewQueue: React.FC<ImportPreviewQueueProps> = ({
  onComplete
}) => {
  const dk = useDarkClasses();
  const [records, setRecords] = useState<ImportedBookRecord[]>(mockImportedRecords);
  const [filter, setFilter] = useState<'all' | 'mapped' | 'pending'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredRecords = records.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return r.title.toLowerCase().includes(q) || r.authorName.toLowerCase().includes(q);
    }
    return true;
  });
  const handleAction = (id: string, action: 'accept' | 'skip') => {
    setRecords(prev => prev.map(r => {
      if (r.id === id) {
        return {
          ...r,
          status: action === 'accept' ? 'imported' : 'error'
        };
      }
      return r;
    }));
  };
  const handleBulkAction = (action: 'accept_all' | 'skip_all') => {
    setRecords(prev => prev.map(r => {
      if (r.status === 'mapped' || r.status === 'pending') {
        return {
          ...r,
          status: action === 'accept_all' ? 'imported' : 'error'
        };
      }
      return r;
    }));
  };
  const pendingCount = records.filter(r => r.status === 'mapped' || r.status === 'pending').length;
  return <div className="space-y-6" data-id="element-10258" data-mp="3cizh1vtcw0mx">
      {/* Toolbar */}
      <div className={`p-4 rounded-xl border ${dk.card} ${dk.borderSubtle} flex flex-col sm:flex-row sm:items-center justify-between gap-4`} data-id="element-10259" data-mp="37l7snbrx6wnh">
        <div className="flex items-center gap-4" data-id="element-10260" data-mp="2xw0twd1j7se0">
          <div className="relative" data-id="element-10261" data-mp="31zoz4p2oybw3">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-10262" data-mp="31zyu6r2lf98n" />
            <input type="text" placeholder="Search books..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-9 pr-4 py-2 rounded-lg text-sm border ${dk.isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-indigo-500`} data-id="element-10263" data-mp="2o1qyemhamdz2" />
          </div>
          <div className="flex items-center gap-2" data-id="element-10264" data-mp="3bvil0z2avhqj">
            <FilterIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-10265" data-mp="3h1jubaou7sz" />
            <select value={filter} onChange={e => setFilter(e.target.value as any)} className={`text-sm bg-transparent border-none focus:ring-0 cursor-pointer ${dk.textSecondary}`} data-id="element-10266" data-mp="3fqhmfww583z">
              <option value="all" data-id="element-10267" data-mp="1o0drba27mguc">All Statuses</option>
              <option value="mapped" data-id="element-10268" data-mp="1nxhszkmg9q5d">Ready to Import</option>
              <option value="pending" data-id="element-10269" data-mp="21pxrc84eozzy">Needs Review</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-10270" data-mp="1xqoyr6l3r85z">
          <button onClick={() => handleBulkAction('skip_all')} className={`px-4 py-2 text-sm font-medium rounded-lg border ${dk.border} ${dk.textSecondary} hover:${dk.bgHover} transition-colors`} data-id="element-10271" data-mp="19zjgkkrow3si">
            Skip Remaining
          </button>
          <button onClick={() => handleBulkAction('accept_all')} className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors" data-id="element-10272" data-mp="1iyepi4m4brzi">
            Import Remaining ({pendingCount})
          </button>
        </div>
      </div>

      {/* Queue List */}
      <div className="space-y-4" data-id="element-10273" data-mp="3kr9nqnwf3gak">
        {filteredRecords.map(record => <div key={record.id} className={`p-4 sm:p-6 rounded-xl border ${dk.card} ${record.status === 'imported' ? 'border-green-500/30 bg-green-50/30 dark:bg-green-900/10' : record.status === 'error' ? 'border-red-500/30 bg-red-50/30 dark:bg-red-900/10 opacity-50' : record.status === 'pending' ? 'border-amber-500/50' : dk.border} transition-all`} data-id="element-10274" data-mp="3ppuqatz56c26">
            <div className="flex flex-col sm:flex-row gap-6" data-id="element-10275" data-mp="7idn0rj787pz">
              {/* Cover or Placeholder */}
              <div className="shrink-0 w-20 h-32 sm:w-24 sm:h-36 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-700" data-id="element-10276" data-mp="cgyg87019r4x">
                {record.coverUrl ? <img src={record.coverUrl} alt={record.title} className="w-full h-full object-cover" data-id="element-10277" data-mp="r98lkxvwcn64" /> : <BookOpenIcon className={`w-8 h-8 ${dk.textMuted}`} data-id="element-10278" data-mp="2fsw8mv0xc6c3" />}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0 flex flex-col" data-id="element-10279" data-mp="2bscfe2jh1mv8">
                <div className="flex items-start justify-between gap-4 mb-2" data-id="element-10280" data-mp="26s7ywcikcs50">
                  <div data-id="element-10281" data-mp="21slmg5ak7fx9">
                    <h4 className={`text-lg font-bold truncate ${dk.textHeading}`} data-id="element-10282" data-mp="21tddr3x4irhq">
                      {record.title}
                    </h4>
                    <p className={`text-sm ${dk.textSecondary}`} data-id="element-10283" data-mp="2uhsi8wimpret">
                      by {record.authorName}
                    </p>
                  </div>

                  {/* Status Badge */}
                  <div className="shrink-0" data-id="element-10284" data-mp="2kpwizi4t3v90">
                    {record.status === 'imported' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" data-id="element-10285" data-mp="3i7d9s3sulwzp">
                        <CheckIcon className="w-3 h-3 mr-1" data-id="element-10286" data-mp="1bht6jv8tll1f" /> Imported
                      </span>}
                    {record.status === 'error' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-id="element-10287" data-mp="11s9naoh73p61">
                        <XIcon className="w-3 h-3 mr-1" data-id="element-10288" data-mp="16p8gn4t1bs36" /> Skipped
                      </span>}
                    {record.status === 'mapped' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" data-id="element-10289" data-mp="wtycaaf5z8m8">
                        Ready
                      </span>}
                    {record.status === 'pending' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" data-id="element-10290" data-mp="2d7yejs1hfacn">
                        <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-10291" data-mp="2sb84e3v1i0bv" /> Needs
                        Review
                      </span>}
                  </div>
                </div>

                {/* Reading Data */}
                <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4" data-id="element-10292" data-mp="314l5vu766a9k">
                  {record.myRating !== undefined && record.myRating > 0 && <div className="flex items-center" data-id="element-10293" data-mp="3azv7whxkihnc">
                      <div className="flex text-yellow-400" data-id="element-10294" data-mp="3b2r3w0puuw95">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < record.myRating! ? 'fill-current' : 'text-gray-300 dark:text-gray-600'}`} data-id="element-10295" data-mp="3k1cos2isund3" data-mpi={i} />)}
                      </div>
                    </div>}

                  {record.genre && <span className={`text-xs px-2 py-0.5 rounded-md font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300`} data-id="element-10296" data-mp="1lo2wkdw4g4hz">
                      {record.genre}
                    </span>}

                  {record.seriesStatus && <span className={`text-xs px-2 py-0.5 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-10297" data-mp="2ny35cgj1qdej">
                      {record.seriesStatus}
                    </span>}

                  {record.bookshelves && record.bookshelves.length > 0 && <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10298" data-mp="207fj0q8dvl8g">
                      {record.bookshelves.map(shelf => <span key={shelf} className={`text-xs px-2 py-0.5 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-10299" data-mp="3bo5vus7bbbb1">
                          {shelf}
                        </span>)}
                    </div>}

                  {record.dateRead && <span className={`text-xs ${dk.textMuted} flex items-center`} data-id="element-10300" data-mp="3h0m211ex6l52">
                      Read: {record.dateRead}
                    </span>}
                </div>

                {record.myReview && <div className={`text-sm italic line-clamp-2 mb-4 ${dk.textSecondary} border-l-2 border-indigo-500 pl-3`} data-id="element-10301" data-mp="331cok9i3m54s">
                    "{record.myReview}"
                  </div>}

                {/* Actions */}
                <div className="mt-auto flex items-center justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-10302" data-mp="2u2rb2srhhgav">
                  {record.status === 'mapped' || record.status === 'pending' ? <>
                      <button onClick={() => handleAction(record.id, 'skip')} className={`p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors`} title="Skip" data-id="element-10303" data-mp="1dml03cqcb9bl">
                        <XIcon className="w-5 h-5" data-id="element-10304" data-mp="1ilxow30ruqgt" />
                      </button>
                      <button className={`p-2 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors`} title="Edit Details" data-id="element-10305" data-mp="uwd033hxsyzm">
                        <Edit2Icon className="w-5 h-5" data-id="element-10306" data-mp="14nqwh2eldioj" />
                      </button>
                      <button onClick={() => handleAction(record.id, 'accept')} className={`flex items-center px-4 py-2 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors`} data-id="element-10307" data-mp="zu4uc4620x6z">
                        <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-10308" data-mp="2itci8gsica4n" />
                        Add to Bookshelf
                      </button>
                    </> : <button onClick={() => handleAction(record.id, record.status === 'imported' ? 'skip' : 'accept')} className={`text-sm font-medium underline ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-10309" data-mp="29u8xl8dozmo1">
                      Undo
                    </button>}
                </div>
              </div>
            </div>
          </div>)}
      </div>

      {filteredRecords.length === 0 && <div className={`text-center py-12 border-2 border-dashed rounded-xl ${dk.border} ${dk.textMuted}`} data-id="element-10310" data-mp="1h4s92lt70px3">
          <BookOpenIcon className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-10311" data-mp="1m5pr8ye7djyl" />
          <p data-id="element-10312" data-mp="3ntk84v5k5vth">No books found matching your criteria.</p>
        </div>}

      {pendingCount === 0 && records.length > 0 && <div className="flex justify-center mt-8" data-id="element-10313" data-mp="3iur37r4c7d4x">
          <button onClick={onComplete} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg transition-transform hover:-translate-y-0.5" data-id="element-10314" data-mp="3e2zapb1mexc5">
            Finish & Go to Bookshelf
          </button>
        </div>}
    </div>;
};